import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useUser } from '@clerk/nextjs';
import { useSessionStorage } from '@uidotdev/usehooks';
import { useAppParams } from '@/hooks/use-app-params';
import { type PortfolioTrialArm, readPortfolioTrialArm } from '@/lib/feature-flags/portfolio-trial-arm-stamp';
import { Analytics } from './index';

/**
 * Signed-out renders send no identity at all, so every field is optional. Spelling that out keeps
 * `userData.user_id` readable without narrowing a union at each call site.
 */
interface AnalyticsUserData {
  email?: string;
  external_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  portfolio_trial_start_arm?: PortfolioTrialArm;
  user_id?: string;
}
export function useAnalytics() {
  const $ = _c(33);
  const {
    user,
    isLoaded
  } = useUser();
  const appParams = useAppParams();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [, setSourceEvent] = useSessionStorage("__source_event", t0);
  let t1;
  bb0: {
    if (!(isLoaded && user)) {
      let t2;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {};
        $[1] = t2;
      } else {
        t2 = $[1];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[2] !== user.unsafeMetadata) {
      t2 = readPortfolioTrialArm(user.unsafeMetadata);
      $[2] = user.unsafeMetadata;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const portfolioTrialArm = t2;
    const t3 = user.emailAddresses?.[0]?.emailAddress;
    let t4;
    if ($[4] !== portfolioTrialArm) {
      t4 = portfolioTrialArm == null ? {} : {
        portfolio_trial_start_arm: portfolioTrialArm
      };
      $[4] = portfolioTrialArm;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t3 || $[7] !== t4 || $[8] !== user.externalId || $[9] !== user.firstName || $[10] !== user.id || $[11] !== user.lastName) {
      t5 = {
        user_id: user.id,
        external_id: user.externalId,
        email: t3,
        first_name: user.firstName,
        last_name: user.lastName,
        ...t4
      };
      $[6] = t3;
      $[7] = t4;
      $[8] = user.externalId;
      $[9] = user.firstName;
      $[10] = user.id;
      $[11] = user.lastName;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t1 = t5;
  }
  const userData = t1;
  let t2;
  if ($[13] !== appParams.externshipId || $[14] !== appParams.moduleId || $[15] !== appParams.stepId || $[16] !== appParams.substepId) {
    t2 = Object.fromEntries(Object.entries({
      externship_id: appParams.externshipId,
      externship_module_id: appParams.moduleId,
      externship_step_id: appParams.stepId,
      externship_substep_id: appParams.substepId
    }).filter(_temp));
    $[13] = appParams.externshipId;
    $[14] = appParams.moduleId;
    $[15] = appParams.stepId;
    $[16] = appParams.substepId;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  const appParamsData = t2;
  let t3;
  if ($[18] !== appParamsData || $[19] !== userData) {
    t3 = (event, properties, callback) => {
      Analytics.track(event, {
        ...appParamsData,
        ...userData,
        ...properties,
        timestamp: new Date().toISOString()
      }, undefined, callback);
    };
    $[18] = appParamsData;
    $[19] = userData;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  const track = t3;
  let t4;
  if ($[21] !== appParamsData || $[22] !== userData) {
    t4 = (page, properties_0) => {
      Analytics.page(page, {
        ...appParamsData,
        ...properties_0,
        ...userData,
        timestamp: new Date().toISOString()
      });
    };
    $[21] = appParamsData;
    $[22] = userData;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const page_0 = t4;
  let t5;
  if ($[24] !== setSourceEvent) {
    t5 = (event_0, properties_1) => {
      setSourceEvent({
        source_event: event_0,
        ...properties_1
      });
    };
    $[24] = setSourceEvent;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const trackSourceEvent = t5;
  let t6;
  if ($[26] !== userData) {
    t6 = properties_2 => {
      Analytics.identify(userData.user_id, {
        ...userData,
        ...properties_2
      });
    };
    $[26] = userData;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const identify = t6;
  let t7;
  if ($[28] !== identify || $[29] !== page_0 || $[30] !== track || $[31] !== trackSourceEvent) {
    t7 = {
      track,
      page: page_0,
      identify,
      trackSourceEvent
    };
    $[28] = identify;
    $[29] = page_0;
    $[30] = track;
    $[31] = trackSourceEvent;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  return t7;
}
function _temp(t0) {
  const [, value] = t0;
  return value;
}